import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useWaitlist } from "../../hooks/useWaitlist";
import { getWaitlistStats } from "../../lib/supabase";
import { MailIcon, UserIcon, ArrowRightIcon, CheckIcon, StarIcon } from "./Icons";

// live count — fetched once, re-fetches after a successful submission
function useLiveCount(deps = []) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    getWaitlistStats().then(({ total }) => {
      if (total > 0) setCount(total);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return count !== null ? count.toLocaleString() + "+" : "1,200+";
}

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner({ size = 15, color = "#050508" }) {
  return (
    <span style={{
      width: size, height: size, flexShrink: 0,
      border: `2px solid rgba(0,0,0,0.25)`,
      borderTopColor: color,
      borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.65s linear infinite",
    }} />
  );
}

// ─── Rocket SVG ───────────────────────────────────────────────────────────────
function RocketSVG({ size = 72 }) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 60 72" fill="none">
      <defs>
        <linearGradient id="rg1" x1="30" y1="0" x2="30" y2="62" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#A7F3D0" />
          <stop offset="40%"  stopColor="#10B77F" />
          <stop offset="100%" stopColor="#047857" />
        </linearGradient>
        <linearGradient id="rg2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="rgba(255,255,255,0.25)" />
          <stop offset="100%"stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <radialGradient id="rg3" cx="40%" cy="35%" r="60%">
          <stop offset="0%"  stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%"stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>

      {/* Body */}
      <path d="M30 2 C24 10 20 22 20 36 L20 52 L30 56 L40 52 L40 36 C40 22 36 10 30 2Z"
        fill="url(#rg1)" />
      {/* Nose cone highlight */}
      <path d="M30 2 C28 8 27 14 27 20 C28 18 30 14 30 2Z" fill="url(#rg2)" opacity="0.7" />
      {/* Body gloss */}
      <ellipse cx="26" cy="28" rx="3" ry="8" fill="url(#rg3)" opacity="0.5" transform="rotate(-5 26 28)" />
      {/* Left fin */}
      <path d="M20 40 L10 58 L20 54Z" fill="#065F46" />
      <path d="M20 40 L13 55 L20 54Z" fill="#059669" opacity="0.6" />
      {/* Right fin */}
      <path d="M40 40 L50 58 L40 54Z" fill="#065F46" />
      <path d="M40 40 L47 55 L40 54Z" fill="#059669" opacity="0.6" />
      {/* Thruster nozzle */}
      <ellipse cx="30" cy="55" rx="8" ry="4" fill="#022C22" />
      <ellipse cx="30" cy="54" rx="6" ry="3" fill="#047857" />
      {/* Window */}
      <circle cx="30" cy="32" r="7.5" fill="rgba(5,5,8,0.8)" stroke="rgba(110,231,183,0.7)" strokeWidth="1.2" />
      <circle cx="30" cy="32" r="5.5" fill="rgba(16,183,127,0.15)" />
      <circle cx="27.5" cy="29.5" r="2" fill="rgba(255,255,255,0.18)" />
      {/* Flames */}
      <ellipse cx="30" cy="60" rx="7" ry="5" fill="#FB923C"
        style={{ animation: "flameBurn 0.12s ease-in-out infinite", transformOrigin: "30px 60px" }} />
      <ellipse cx="30" cy="62" rx="5" ry="4.5" fill="#FBBF24"
        style={{ animation: "flameBurn 0.18s ease-in-out infinite reverse", transformOrigin: "30px 62px" }} />
      <ellipse cx="30" cy="64" rx="2.5" ry="3" fill="rgba(255,255,255,0.9)"
        style={{ animation: "flameBurn 0.09s ease-in-out infinite", transformOrigin: "30px 64px" }} />
    </svg>
  );
}

// ─── AlphaStack Flag (rendered separately below the rocket during launch) ─────
function AlphaFlag() {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 0,
      animation: "flagWave 1.2s ease-in-out infinite",
      transformOrigin: "left center",
      marginTop: 6,
    }}>
      {/* Pole */}
      <div style={{
        width: 2, height: 36,
        background: "linear-gradient(to bottom, rgba(110,231,183,0.9), rgba(110,231,183,0.3))",
        borderRadius: 1, flexShrink: 0,
      }} />
      {/* Flag body */}
      <div style={{
        background: "linear-gradient(135deg, #10B77F, #047857)",
        borderRadius: "0 8px 8px 0",
        padding: "5px 12px",
        display: "flex", alignItems: "center", gap: 7,
        boxShadow: "0 0 18px rgba(16,183,127,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
        border: "1px solid rgba(110,231,183,0.3)",
      }}>
        {/* Mini stacked-layers icon */}
        <div style={{ display: "flex", flexDirection: "column", gap: 1.5, flexShrink: 0 }}>
          {[14, 12, 10, 8, 6].map((w, i) => (
            <div key={i} style={{
              width: w, height: 2.5, borderRadius: 1,
              background: "rgba(255,255,255,0.9)",
            }} />
          ))}
        </div>
        {/* Text */}
        <span style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 900, fontSize: "0.72rem",
          color: "#fff", letterSpacing: "0.03em",
          whiteSpace: "nowrap",
          textShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          Alpha<span style={{ color: "#A7F3D0" }}>Stack</span>
        </span>
      </div>
    </div>
  );
}

// ─── Stars field for launch overlay ──────────────────────────────────────────
const STARS = Array.from({ length: 28 }, (_, i) => ({
  x:    5 + (i * 137.5) % 90,
  y:    3 + (i * 73.1)  % 85,
  s:    0.8 + (i % 4) * 0.5,
  d:    (i % 5) * 0.3,
  dur:  1.2 + (i % 4) * 0.6,
}));

// ─── Full-viewport rocket launch overlay ─────────────────────────────────────
// Uses createPortal → renders directly on document.body so it is NEVER
// clipped, filtered, or z-index-stacked by any parent element.
function RocketLaunchOverlay({ onComplete }) {
  useEffect(() => {
    const t = setTimeout(onComplete, 2800);
    return () => clearTimeout(t);
  }, [onComplete]);

  const CONFETTI = [
    { cx: "-60px", cy: "-50px", cr: "190deg",  color: "#10B77F", sz: 8,  d: "0s"    },
    { cx: "55px",  cy: "-45px", cr: "-180deg", color: "#6EE7B7", sz: 6,  d: "0.05s" },
    { cx: "-38px", cy: "-68px", cr: "260deg",  color: "#60A5FA", sz: 7,  d: "0.1s"  },
    { cx: "65px",  cy: "-38px", cr: "-230deg", color: "#FBBF24", sz: 5,  d: "0.08s" },
    { cx: "16px",  cy: "-75px", cr: "310deg",  color: "#A7F3D0", sz: 4,  d: "0.15s" },
    { cx: "-70px", cy: "-32px", cr: "-290deg", color: "#4ADE80", sz: 6,  d: "0.04s" },
    { cx: "38px",  cy: "-72px", cr: "170deg",  color: "#10B77F", sz: 5,  d: "0.12s" },
    { cx: "-18px", cy: "-80px", cr: "-140deg", color: "#6EE7B7", sz: 7,  d: "0.06s" },
    { cx: "-82px", cy: "-28px", cr: "230deg",  color: "#FB923C", sz: 5,  d: "0.09s" },
    { cx: "78px",  cy: "-36px", cr: "-210deg", color: "#F0EDE8", sz: 4,  d: "0.14s" },
  ];

  const overlay = (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      zIndex: 99999,                          /* above everything */
      background: "rgba(5,5,8,0.82)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      animation: "fadeIn 0.22s ease both",
    }}>

      {/* ── Stars scattered in sky ── */}
      {STARS.map((s, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${s.x}%`, top: `${s.y * 0.65}%`,
          width: s.s, height: s.s, borderRadius: "50%",
          background: "rgba(255,255,255,0.9)",
          animation: `twinkle ${s.dur}s ease-in-out ${s.d}s infinite`,
        }} />
      ))}
      {[8, 22, 42, 62, 78, 92].map((x, i) => (
        <div key={`gs${i}`} style={{
          position: "absolute", left: `${x}%`, top: `${4 + i * 8}%`,
          width: 3, height: 3, borderRadius: "50%",
          background: "rgba(110,231,183,0.9)",
          boxShadow: "0 0 8px rgba(16,183,127,0.8)",
          animation: `twinkle ${1.4 + i * 0.3}s ease-in-out ${i * 0.18}s infinite`,
        }} />
      ))}

      {/* ── Launch point: fixed bottom-center of viewport ── */}
      <div style={{
        position: "absolute",
        bottom: "24%", left: "50%",
        transform: "translateX(-50%)",
        width: 0,               /* zero-width anchor; children use absolute/relative */
      }}>

        {/* Expanding rings */}
        {[0, 0.16, 0.32].map((delay, i) => (
          <div key={i} style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: 100, height: 100, borderRadius: "50%",
            border: `${2.2 - i * 0.5}px solid rgba(16,183,127,${0.75 - i * 0.2})`,
            boxShadow: `0 0 24px rgba(16,183,127,${0.45 - i * 0.12})`,
            animation: "launchRingExpand 1s ease-out forwards",
            animationDelay: `${delay}s`,
          }} />
        ))}

        {/* Ground bloom glow */}
        <div style={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 260, height: 260, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(16,183,127,0.3) 0%, transparent 65%)",
          filter: "blur(22px)",
          animation: "launchRingExpand 1.5s ease-out forwards",
        }} />

        {/* Confetti */}
        {CONFETTI.map((c, i) => (
          <div key={i} style={{
            position: "absolute", top: "50%", left: "50%",
            width: c.sz, height: c.sz,
            borderRadius: c.sz > 5 ? 2 : "50%",
            background: c.color, boxShadow: `0 0 6px ${c.color}`,
            "--cx": c.cx, "--cy": c.cy, "--cr": c.cr,
            animation: `confettiPop 1.1s cubic-bezier(0.1,0,0.8,1) ${c.d} both`,
          }} />
        ))}

        {/* ── Rocket + Flag: STRAIGHT UP ── */}
        <div style={{
          position: "absolute",
          left: "50%",
          top: 0,
          transform: "translateX(-50%)",
          animation: "rocketLift 2.4s cubic-bezier(0.12, 0, 0.22, 1) 0.12s forwards",
          filter: "drop-shadow(0 0 24px rgba(16,183,127,0.9)) drop-shadow(0 0 55px rgba(16,183,127,0.4))",
          zIndex: 10,
          display: "flex", flexDirection: "column", alignItems: "center",
        }}>
          <RocketSVG size={84} />
          <AlphaFlag />
        </div>

        {/* ── Trail beam — straight down, grows with rocket ── */}
        <div style={{
          position: "absolute",
          top: "72px",           /* starts just below rocket nozzle */
          left: "50%",
          transform: "translateX(-50%)",
          width: 5,
          borderRadius: "0 0 4px 4px",
          background: "linear-gradient(to bottom, rgba(255,180,50,1), rgba(251,146,60,0.75) 12%, rgba(16,183,127,0.55) 45%, rgba(16,183,127,0.18) 75%, transparent)",
          filter: "blur(1.8px)",
          boxShadow: "0 0 10px rgba(251,146,60,0.5), 0 0 26px rgba(16,183,127,0.3)",
          animation: "trailGrow 2.5s cubic-bezier(0.08,0,0.35,1) 0.14s forwards",
          zIndex: 9,
        }} />

        {/* Smoke puffs at base */}
        {[0, 1, 2, 3, 4, 5].map(i => {
          const a = (i / 6) * Math.PI * 2;
          const r = 18 + (i % 3) * 12;
          return (
            <div key={`sm${i}`} style={{
              position: "absolute",
              top: "50%", left: "50%",
              width: 9 - (i % 3) * 2, height: 9 - (i % 3) * 2,
              borderRadius: "50%",
              background: `rgba(16,183,127,${0.45 - i * 0.06})`,
              transform: `translate(
                calc(-50% + ${Math.cos(a) * r}px),
                calc(-50% + ${Math.sin(a) * r * 0.35}px)
              )`,
              filter: "blur(3px)",
              animation: `particleDrift ${1.1 + i * 0.18}s ease-out ${i * 0.09}s infinite`,
            }} />
          );
        })}
      </div>

      {/* Status label */}
      <div style={{
        position: "absolute", bottom: "10%", left: "50%",
        transform: "translateX(-50%)",
        fontFamily: "Space Grotesk, sans-serif",
        fontWeight: 700, fontSize: "0.88rem",
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: "rgba(110,231,183,0.9)",
        textShadow: "0 0 20px rgba(16,183,127,0.7)",
        animation: "fadeUp 0.5s ease 0.35s both",
        whiteSpace: "nowrap",
      }}>
        🚀 Launching into orbit...
      </div>
    </div>
  );

  // Portal renders into document.body — breaks out of ALL parent
  // stacking contexts, transforms, and overflow constraints.
  return createPortal(overlay, document.body);
}

// ─── Success Card ─────────────────────────────────────────────────────────────
function SuccessCard({ isHero, liveCount = "1,200+" }) {
  // Confetti around the card
  const CHIPS = [
    { cx: "-60px", cy: "-50px", cr: "190deg",  color: "#10B77F", sz: 9,  d: "0.05s" },
    { cx: "55px",  cy: "-55px", cr: "-170deg", color: "#6EE7B7", sz: 6,  d: "0.12s" },
    { cx: "-40px", cy: "-72px", cr: "250deg",  color: "#60A5FA", sz: 7,  d: "0.08s" },
    { cx: "68px",  cy: "-38px", cr: "-220deg", color: "#FBBF24", sz: 5,  d: "0.18s" },
    { cx: "20px",  cy: "-80px", cr: "300deg",  color: "#A7F3D0", sz: 4,  d: "0.14s" },
    { cx: "-70px", cy: "-32px", cr: "-280deg", color: "#4ADE80", sz: 6,  d: "0.06s" },
  ];

  return (
    <div style={{ position: "relative", display: "flex", justifyContent: "center" }}>
      {/* Confetti */}
      <div style={{ position: "absolute", top: "50%", left: "50%", pointerEvents: "none" }}>
        {CHIPS.map((c, i) => (
          <div key={i} style={{
            position: "absolute",
            width: c.sz, height: c.sz,
            borderRadius: 2,
            background: c.color,
            boxShadow: `0 0 6px ${c.color}`,
            "--cx": c.cx, "--cy": c.cy, "--cr": c.cr,
            animation: `confettiPop 0.8s cubic-bezier(0.15,0,0.75,1) ${c.d} both`,
          }} />
        ))}
      </div>

      {/* Card */}
      <div style={{
        width: "100%",
        textAlign: "center",
        padding: isHero ? "40px 32px" : "32px 24px",
        borderRadius: 20,
        background: "rgba(5,5,8,0.92)",
        border: "1px solid rgba(16,183,127,0.38)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: `
          0 0 0 1px rgba(16,183,127,0.08) inset,
          0 0 60px rgba(16,183,127,0.12),
          0 24px 64px rgba(0,0,0,0.6)
        `,
        position: "relative",
        overflow: "hidden",
        animation: "successBounceIn 0.65s cubic-bezier(0.16,1,0.3,1) both",
      }}>
        {/* Top shimmer line */}
        <div style={{
          position: "absolute", top: 0, left: "10%", right: "10%", height: 1,
          background: "linear-gradient(90deg, transparent, rgba(16,183,127,0.65), transparent)",
        }} />

        {/* Aurora glow */}
        <div style={{
          position: "absolute", top: "-30%", left: "50%",
          transform: "translateX(-50%)",
          width: 300, height: 180,
          background: "radial-gradient(ellipse, rgba(16,183,127,0.14) 0%, transparent 70%)",
          filter: "blur(30px)", pointerEvents: "none",
          animation: "auroraFloat1 5s ease-in-out infinite",
        }} />

        {/* Check icon */}
        <div style={{ position: "relative", display: "inline-block", marginBottom: 22 }}>
          {/* Pulse rings */}
          {[0.2, 0.45, 0.7].map((delay, i) => (
            <div key={i} style={{
              position: "absolute",
              inset: -(i + 1) * 8,
              borderRadius: "50%",
              border: `1.5px solid rgba(16,183,127,${0.5 - i * 0.12})`,
              animation: `successRingPulse 1.2s ease-out ${delay}s both`,
            }} />
          ))}
          <div style={{
            width: 66, height: 66, borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(16,183,127,0.25), rgba(16,183,127,0.1))",
            border: "1.5px solid rgba(16,183,127,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--gold)",
            boxShadow: "0 0 30px rgba(16,183,127,0.3)",
            animation: "successBounceIn 0.7s cubic-bezier(0.16,1,0.3,1) 0.1s both",
          }}>
            <CheckIcon size={28} />
          </div>
        </div>

        {/* Heading */}
        <h3 style={{
          fontFamily: "Space Grotesk, sans-serif",
          fontSize: isHero ? "1.35rem" : "1.1rem",
          fontWeight: 800, marginBottom: 10,
          color: "var(--text)",
          letterSpacing: "-0.02em",
          animation: "fadeUp 0.55s ease 0.28s both",
        }}>
          You're on the list! 🎉
        </h3>

        <p style={{
          fontSize: "0.92rem", color: "var(--text-dim)",
          lineHeight: 1.65, marginBottom: 22,
          animation: "fadeUp 0.55s ease 0.4s both",
        }}>
          We'll notify you the moment early access opens. <br />
          Watch your inbox for updates.
        </p>

        {/* Stats row */}
        <div style={{
          display: "flex", gap: 0,
          borderRadius: 12, overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.06)",
          marginBottom: 22,
          animation: "fadeUp 0.5s ease 0.52s both",
        }}>
          {[
            { val: liveCount, label: "Waitlist" },
            { val: "15",     label: "Days Left" },
            { val: "#1",     label: "Priority" },
          ].map(({ val, label }, i, arr) => (
            <div key={label} style={{
              flex: 1, padding: "12px 8px", textAlign: "center",
              background: "rgba(16,183,127,0.04)",
              borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
            }}>
              <div style={{
                fontFamily: "Space Grotesk, sans-serif",
                fontWeight: 800, fontSize: "1.1rem",
                color: "var(--gold)", letterSpacing: "-0.02em",
              }}>
                {val}
              </div>
              <div style={{
                fontSize: "0.65rem", color: "var(--text-muted)",
                letterSpacing: "0.08em", textTransform: "uppercase",
                fontWeight: 600, marginTop: 2,
              }}>
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Stars + tagline */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          animation: "fadeIn 0.5s ease 0.65s both",
        }}>
          {[1,2,3,4,5].map(i => (
            <StarIcon key={i} size={13} style={{ color: "var(--gold)" }} />
          ))}
          <span style={{
            fontSize: "0.72rem", color: "var(--text-muted)",
            marginLeft: 6, fontWeight: 500,
          }}>
           
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Duplicate Card ───────────────────────────────────────────────────────────
function DuplicateCard() {
  return (
    <div style={{
      textAlign: "center", padding: "28px 24px", borderRadius: 16,
      background: "rgba(10,10,16,0.9)",
      border: "1px solid rgba(16,183,127,0.22)",
      backdropFilter: "blur(16px)",
      animation: "fadeIn 0.4s ease both",
    }}>
      <div style={{
        fontSize: "2rem", marginBottom: 12,
        animation: "successBounceIn 0.6s cubic-bezier(0.16,1,0.3,1) both",
      }}>
        ⚡
      </div>
      <h3 style={{
        fontFamily: "Space Grotesk, sans-serif",
        fontSize: "1rem", fontWeight: 700, marginBottom: 6, color: "var(--gold)",
      }}>
        Already registered!
      </h3>
      <p style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
        This email is already on our waitlist. You're good!
      </p>
    </div>
  );
}

// ─── Input field ──────────────────────────────────────────────────────────────
function Field({ icon: Icon, type = "text", placeholder, value, onChange, onKeyDown, error, disabled, autoComplete, label }) {
  const [focused, setFocused] = useState(false);

  return (
    <div>
      {label && (
        <label style={{
          display: "block", fontSize: "0.72rem",
          fontFamily: "Space Grotesk, sans-serif",
          fontWeight: 700, color: focused ? "var(--gold)" : "var(--text-muted)",
          letterSpacing: "0.1em", textTransform: "uppercase",
          marginBottom: 7, transition: "color 0.2s",
        }}>
          {label}
        </label>
      )}
      <div className="input-icon-wrap">
        {Icon && <span className="icon-prefix"><Icon size={15} /></span>}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`input${error ? " has-error" : ""}`}
          style={{
            background: focused
              ? "rgba(16,183,127,0.07)"
              : "rgba(255,255,255,0.055)",
            border: `1px solid ${error
              ? "rgba(248,113,113,0.5)"
              : focused
                ? "rgba(16,183,127,0.6)"
                : "rgba(255,255,255,0.14)"}`,
            boxShadow: focused
              ? "0 0 0 3px rgba(16,183,127,0.12), 0 2px 12px rgba(0,0,0,0.3)"
              : "0 2px 8px rgba(0,0,0,0.2)",
            color: "var(--text)",
            fontSize: "0.92rem",
            transition: "all 0.22s ease",
          }}
        />
      </div>
      {error && (
        <p style={{ marginTop: 6, fontSize: "0.73rem", color: "var(--red)" }}>⚠ {error}</p>
      )}
    </div>
  );
}

// ─── Main WaitlistForm ────────────────────────────────────────────────────────
export default function WaitlistForm({ variant = "hero" }) {
  const { fields, setField, status, errors, serverError, handleSubmit, isLoading } = useWaitlist();
  const [animPhase, setAnimPhase] = useState("idle"); // "idle" | "launching" | "success"
  const [btnHovered, setBtnHovered] = useState(false);
  const isHero = variant === "hero";
  // refetch count after each successful submission so number updates live
  const liveCount = useLiveCount([animPhase === "success"]);

  useEffect(() => {
    if ((status === "success" || status === "duplicate") && animPhase === "idle") {
      setAnimPhase("launching");
    }
  }, [status, animPhase]);

  if (animPhase === "launching") {
    return <RocketLaunchOverlay onComplete={() => setAnimPhase("success")} />;
  }
  if (animPhase === "success") {
    if (status === "duplicate") return <DuplicateCard />;
    return <SuccessCard isHero={isHero} liveCount={liveCount} />;
  }

  // ── Normal form ──────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%" }}>
      <div style={{
        background: isHero ? "rgba(5,5,8,0.88)" : "transparent",
        border: isHero ? "1px solid rgba(16,183,127,0.32)" : "none",
        borderRadius: isHero ? 20 : 0,
        padding: isHero ? "30px 28px" : "0",
        backdropFilter: isHero ? "blur(24px)" : "none",
        WebkitBackdropFilter: isHero ? "blur(24px)" : "none",
        boxShadow: isHero
          ? "0 0 0 1px rgba(16,183,127,0.06) inset, 0 0 60px rgba(16,183,127,0.1), 0 24px 64px rgba(0,0,0,0.55)"
          : "none",
        position: "relative",
        overflow: "hidden",
      }}>

        {/* Top accent line */}
        {isHero && (
          <div style={{
            position: "absolute", top: 0, left: "12%", right: "12%", height: 1,
            background: "linear-gradient(90deg, transparent, rgba(16,183,127,0.55), transparent)",
          }} />
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* First name — hero only */}
          {isHero && (
            <Field
              icon={UserIcon}
              placeholder="First name (optional)"
              label="First Name"
              value={fields.firstName}
              onChange={(e) => setField("firstName", e.target.value)}
              disabled={isLoading}
              autoComplete="given-name"
            />
          )}

          {/* Email — hero */}
          {isHero && (
            <Field
              icon={MailIcon}
              type="email"
              placeholder="Enter your email address"
              label="Email Address"
              value={fields.email}
              onChange={(e) => setField("email", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              error={errors.email}
              disabled={isLoading}
              autoComplete="email"
            />
          )}

          {/* CTA (non-hero) inline layout */}
          {!isHero && (
            <div className="waitlist-row" style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <Field
                  icon={MailIcon}
                  type="email"
                  placeholder="Enter your email"
                  value={fields.email}
                  onChange={(e) => setField("email", e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  error={errors.email}
                  disabled={isLoading}
                  autoComplete="email"
                />
              </div>
              <button
                className={`btn-gold${isLoading ? " btn-loading-pulse" : ""}`}
                onClick={handleSubmit}
                disabled={isLoading}
                style={{ padding: "13px 22px", borderRadius: 10, fontSize: "0.9rem", whiteSpace: "nowrap" }}
              >
                {isLoading ? <><Spinner size={14} />Launching...</> : <>Join <ArrowRightIcon size={15} /></>}
              </button>
            </div>
          )}

          {/* Hero submit button */}
          {isHero && (
            <button
              className={`btn-gold${isLoading ? " btn-loading-pulse" : ""}`}
              onClick={handleSubmit}
              disabled={isLoading}
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              style={{
                padding: "16px 24px",
                borderRadius: 13,
                fontSize: "1rem",
                width: "100%",
                fontWeight: 700,
                letterSpacing: "0.01em",
                position: "relative",
                overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: btnHovered
                  ? "0 0 0 2px rgba(16,183,127,0.4), 0 8px 40px rgba(16,183,127,0.5)"
                  : "0 4px 24px rgba(16,183,127,0.3)",
                transform: btnHovered ? "translateY(-2px)" : "translateY(0)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
            >
              {isLoading ? (
                <><Spinner size={16} />Launching your spot...</>
              ) : (
                <>
                  {btnHovered ? "🚀" : "✦"} Get Early Access
                  <ArrowRightIcon size={16} />
                </>
              )}
            </button>
          )}

          {serverError && (
            <p style={{ fontSize: "0.78rem", color: "var(--red)", textAlign: "center" }}>
              {serverError}
            </p>
          )}
        </div>

        {/* Hero footer */}
        {isHero && (
          <div style={{
            marginTop: 20, paddingTop: 18,
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex", justifyContent: "space-between",
            alignItems: "center", flexWrap: "wrap", gap: 8,
          }}>
            <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
              <b style={{ color: "var(--gold)" }}>{liveCount}</b> investors joined
            </span>
          </div>
        )}
      </div>

      
    </div>
  );
}
